import { useEffect, useId, useMemo, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { DialogContent, Divider } from '@mui/material';
import { getObjectChanges, isObjectNullOrEmpty } from '@/Infrastructure/Utilities/object.type';
import { getApiFormFieldErrors } from '@api/getApiError';
import { useAddLocationMutation, useFetchLocationQuery, useUpdateLocationMutation } from '@api/Trinium/Endpoints/Location/slice';
import { useUpdateJobLocationsMutation } from '@api/Trinium/Endpoints/Job/slice';
import { Location } from '@api/Trinium/Endpoints/Location/types';
import FormikModal from '@components/Modals/FormikModal';
import FormikModalHeader from '@components/Modals/FormikModalHeader';
import { useModalProvider } from '@components/Modals/ModalContext';
import { setFormFieldError } from '@forms/Components/types';
import ButtonDropdown from '@forms/Components/Dropdowns/ButtonDropdown';
import LocationButtonContent from '@forms/Appointment/LocationButtonContent';
import LocationListContent from '@forms/Appointment/LocationListContent';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { isFormikErrorsEmpty } from '@hooks/useFormikValues';
import {
  consigneeApiFieldMappings,
  ConsigneeFormik,
  ConsigneeFormikValidationSchema,
  ConsingneeModalProps,
  mapConsigneeFormToLocation,
  mapLocationToConsigneeForm
} from './types';
import ConsigneeForm from './ConsigneeForm';

import styles from './Consignee.module.css';

export default function ConsigneeModal({ id, enableSelection, title, defaultValues, ...props }: ConsingneeModalProps) {
  const { number: terminalNumber } = useCurrentTerminal();
  const [isOpen, setIsOpen] = useState(true);
  const formId = useId();
  const modalContext = useModalProvider();
  const [consigneeId, setConsigneeId] = useState<string | null>(id ?? null);
  const [addLoc, { isSuccess: addSuccess }] = useAddLocationMutation({
    fixedCacheKey: props.apiFixedCacheKey
  });
  const [updateLoc, { isSuccess: updateSuccess }] = useUpdateLocationMutation();
  const [updateJobLocations, { isSuccess: updateJobLocationsSuccess }] = useUpdateJobLocationsMutation();
  /** When editing from a leg, the edit is only complete once that job's legs have also taken on the saved values */
  const isUpdateComplete = updateSuccess && (props.updateLegsForJobId === undefined || updateJobLocationsSuccess);
  const modalTitle = title ? title : id ? 'Edit Consignee' : 'Add Consignee';
  /** If this is for a "New" Location, id will be undefined and this Api call will be skipped */
  const { data: location, isFetching } = useFetchLocationQuery(
    {
      terminalNumber,
      id: String(consigneeId)
    },
    {
      skip: !consigneeId,
      refetchOnMountOrArgChange: true
    }
  );

  const initialValues: ConsigneeFormik | undefined = useMemo(() => {
    // Don't do anything until we determined the API result
    if (isFetching) return undefined;

    if (consigneeId && location) {
      return {
        ...mapLocationToConsigneeForm(location)
      };
    }

    return {
      isTrmUnique: true,
      name: defaultValues?.name ?? '',
      address1: defaultValues?.address1 ?? '',
      address2: defaultValues?.address2 ?? '',
      zip: defaultValues?.zip ?? '',
      area: defaultValues?.area ?? '',
      phone: defaultValues?.phone ?? '',
      fax: defaultValues?.fax ?? '',
      email: defaultValues?.email ?? '',
      notes: defaultValues?.notes ?? '',
      city: defaultValues?.city ?? null,
      country: defaultValues?.country ?? null,
      shippingHours: {
        weekdayFrom: null,
        weekdayTo: null,
        saturdayFrom: null,
        saturdayTo: null,
        sundayFrom: null,
        sundayTo: null
      },
      deliveryHours: {
        weekdayFrom: null,
        weekdayTo: null,
        saturdayFrom: null,
        saturdayTo: null,
        sundayFrom: null,
        sundayTo: null
      },
      usePuNotes: false,
      puNotes: '',
      useDelvNotes: false,
      delvNotes: '',
      contact: undefined
    } as ConsigneeFormik;
  }, [isFetching, location, consigneeId, defaultValues]);

  async function submitForm(values: ConsigneeFormik, formik: FormikHelpers<ConsigneeFormik>) {
    const validationErrors = await formik.validateForm(values);
    if (isFormikErrorsEmpty(validationErrors) && initialValues) {
      if (values.id) {
        const locationId = values.id;
        const valuesToPost = getObjectChanges(values, initialValues);
        if (isObjectNullOrEmpty(valuesToPost)) return;

        void updateLoc({
          terminalNumber: terminalNumber,
          location: {
            id: locationId,
            ...mapConsigneeFormToLocation(valuesToPost)
          }
        })
          .unwrap()
          .then(async (savedLocation) => {
            /** When editing from a leg, carry the saved values to the legs on that job */
            if (props.updateLegsForJobId !== undefined) {
              await updateJobLocations({
                terminalNumber: terminalNumber,
                jobId: props.updateLegsForJobId,
                locations: [savedLocation]
              }).unwrap();
            }
          })
          .catch((ex: unknown) => {
            const errors = getApiFormFieldErrors(consigneeApiFieldMappings, ex);

            errors.forEach((error) => {
              if (error.fieldName) {
                setFormFieldError(error.fieldName, error.errorMessage, formik.setFieldError);
              } else {
                modalContext.queueModalAnnouncement({
                  severity: 'error',
                  message: error.errorMessage
                });
              }
            });
          });
      } else {
        // Add flow: send the full form so pre-populated defaults (e.g., name from EDI) survive
        // even when the user doesn't edit them — getObjectChanges would otherwise strip them.
        void addLoc({
          terminalNumber: terminalNumber,
          location: {
            ...mapConsigneeFormToLocation(values)
          }
        })
          .unwrap()
          .catch((ex: unknown) => {
            const errors = getApiFormFieldErrors(consigneeApiFieldMappings, ex);

            errors.forEach((error) => {
              if (error.fieldName) {
                setFormFieldError(error.fieldName, error.errorMessage, formik.setFieldError);
              } else {
                modalContext.queueModalAnnouncement({
                  severity: 'error',
                  message: error.errorMessage
                });
              }
            });
          });
      }
    }
  }

  /** On successful save, close the modal */
  useEffect(() => {
    if (addSuccess || isUpdateComplete) {
      setIsOpen(false);
    }
  }, [addSuccess, isUpdateComplete]);

  return (
    <FormikModal
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
      paperClass={styles['modal']}>
      {initialValues && (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={ConsigneeFormikValidationSchema}
          onSubmit={submitForm}>
          {(props) => (
            <>
              <FormikModalHeader
                title={modalTitle}
                formName={formId}
                onClose={() => {
                  setIsOpen(false);
                }}
              />

              <DialogContent>
                {enableSelection && (
                  <>
                    <ButtonDropdown
                      buttonConfig={{
                        height: '32px'
                      }}
                      ButtonContent={LocationButtonContent}
                      buttonContentProps={{
                        location: consigneeId && location ? location : null,
                        emptyValueText: '<New Consignee>'
                      }}
                      PopupContent={LocationListContent}
                      popupContentProps={{
                        selectionOnly: true,
                        legLocation: consigneeId && location ? location : null
                      }}
                      fieldOnChange={(value) => {
                        if (value) {
                          setConsigneeId((value as Location).id);
                        }
                      }}
                      onClear={
                        consigneeId
                          ? () => {
                              setConsigneeId(null);
                            }
                          : undefined
                      }
                    />
                    <Divider sx={{ margin: '20px 0' }} />
                  </>
                )}

                <ConsigneeForm
                  id={formId}
                  consigneeId={consigneeId}
                  {...props}
                />
              </DialogContent>
            </>
          )}
        </Formik>
      )}
    </FormikModal>
  );
}
